import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCalendarAlt, faClock, faChevronLeft, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import {
  formatBookingDate,
  formatBookingRangeLabel,
  getBookingCalendarGrid,
  getBookingDateRange,
  getTimeSlots,
  isSameDay,
  isToday,
  toDateKey,
} from '../utils/demoBookingDates';
import { submitContactRequest } from '../services/contact';

const inputStyle =
  'border-2 hover:bg-yellow-50 my-2 p-2 focus:outline-none focus:ring focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded';
const inputStyleErr =
  'border-2 hover:bg-yellow-50 my-2 p-2 focus:outline-none focus:ring focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-red-400 rounded';

const DemoBookingModal = ({ open, onClose }) => {
  const { start, end } = useMemo(() => getBookingDateRange(), []);
  const rangeLabel = useMemo(() => formatBookingRangeLabel(start, end), [start, end]);
  const { weekdayLabels, grid } = useMemo(() => getBookingCalendarGrid(start, end), [start, end]);
  const timeSlots = useMemo(() => getTimeSlots(), []);

  const [step, setStep] = useState('date');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const reset = () => {
    setStep('date');
    setSelectedDate(null);
    setSelectedTime(null);
    setName('');
    setEmail('');
    setPhone('');
    setErrors({});
    setSubmitError('');
    setSubmitted(false);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!open) {
    return null;
  }

  const selectDate = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setStep('time');
  };

  const selectTime = (time) => {
    setSelectedTime(time);
    setStep('details');
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!name.trim()) nextErrors.name = true;
    if (!email.trim()) nextErrors.email = true;
    if (!selectedDate || !selectedTime) {
      setSubmitError('Please select a date and time.');
      return;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSubmitting(true);
    setSubmitError('');

    const bookingMessage = `Demo booking: ${formatBookingDate(selectedDate)} at ${selectedTime} (UK time).`;

    submitContactRequest({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      message: bookingMessage,
    })
      .then(() => {
        setSubmitted(true);
      })
      .catch((err) => {
        setSubmitError(
          err.message === 'unexpected'
            ? 'The server did not respond in an expected way. Please try again or contact support.'
            : 'We could not reach the server, check your internet connection and try again.'
        );
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-booking-title"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-5 border-b border-gray-200">
          <div className="pr-4">
            <h2 id="demo-booking-title" className="text-xl font-bold">
              Book a demo
            </h2>
            <p className="mt-1 text-sm text-gray-600 font-light">
              Pick a weekday and time, then send your details — we will walk you through PunchIn and all of its features live.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-800 p-1"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="p-5">
          {submitted ? (
            <div className="text-center py-6">
              <p className="text-lg font-semibold text-green-700">Success</p>
              <p className="mt-3 text-gray-600 font-light">
                Your message has been forwarded. We will get back to you as soon as possible.
              </p>
              <p className="mt-2 text-sm text-gray-500 font-light">
                {formatBookingDate(selectedDate)} at {selectedTime}
              </p>
              <button
                type="button"
                onClick={handleClose}
                className="mt-6 py-3 px-8 bg-primary hover:bg-primary-darker rounded-lg font-semibold text-white text-sm"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {step !== 'date' && (
                <button
                  type="button"
                  onClick={() => setStep(step === 'details' ? 'time' : 'date')}
                  className="mb-4 text-sm text-blue-700 hover:underline"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="mr-1" />
                  Back
                </button>
              )}

              {step === 'date' && (
                <>
                  <p className="text-gray-600 text-sm font-light mb-3">
                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-blue-600" />
                    Choose a weekday ({rangeLabel})
                  </p>
                  <div className="inline-block w-full max-w-xs mx-auto">
                    <div className="grid grid-cols-5 gap-1 mb-1">
                      {weekdayLabels.map((label) => (
                        <div
                          key={label}
                          className="text-center text-[10px] font-semibold uppercase text-gray-500 py-0.5"
                        >
                          {label}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-5 gap-1">
                      {grid.flat().map((date, index) => {
                        if (!date) {
                          return <div key={`empty-${index}`} className="h-8" aria-hidden="true" />;
                        }
                        const selected = isSameDay(date, selectedDate);
                        const today = isToday(date);
                        return (
                          <button
                            key={toDateKey(date)}
                            type="button"
                            onClick={() => selectDate(date)}
                            className={`h-8 w-8 mx-auto flex items-center justify-center text-xs rounded-full border ${
                              selected
                                ? 'border-blue-600 bg-blue-600 text-white font-semibold'
                                : today
                                  ? 'border-yellow-500 bg-yellow-300 text-gray-900 ring-2 ring-yellow-400 font-semibold'
                                  : 'border-blue-100 hover:border-blue-500 hover:bg-blue-50 text-gray-800'
                            }`}
                            title={formatBookingDate(date)}
                          >
                            {date.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {step === 'time' && selectedDate && (
                <>
                  <p className="text-gray-600 font-light mb-1">
                    {formatBookingDate(selectedDate)}
                  </p>
                  <p className="text-gray-600 font-light mb-4">
                    <FontAwesomeIcon icon={faClock} className="mr-2 text-blue-600" />
                    Choose a 30-minute slot (8:00–17:30)
                  </p>
                  <div className="grid grid-cols-4 gap-2 max-w-sm">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => selectTime(time)}
                        className={`py-2.5 px-2 text-xs rounded border ${
                          selectedTime === time
                            ? 'border-blue-600 bg-blue-600 text-white'
                            : 'border-blue-100 hover:border-blue-500 hover:bg-blue-50 text-gray-800'
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {step === 'details' && selectedDate && selectedTime && (
                <form onSubmit={handleSubmit}>
                  <p className="text-gray-700 mb-4 font-light">
                    <strong>{formatBookingDate(selectedDate)}</strong> at <strong>{selectedTime}</strong>
                  </p>
                  <label className="block text-sm font-medium text-gray-700">
                    Name <span className="text-red-600">*</span>
                    <input
                      className={errors.name ? inputStyleErr : inputStyle}
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Doe"
                    />
                  </label>
                  <label className="block text-sm font-medium text-gray-700">
                    Email <span className="text-red-600">*</span>
                    <input
                      className={errors.email ? inputStyleErr : inputStyle}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="john@example.com"
                    />
                  </label>
                  <label className="block text-sm font-medium text-gray-700">
                    Phone
                    <input
                      className={inputStyle}
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="07891234567"
                    />
                  </label>
                  {submitError && (
                    <p className="text-red-600 text-sm mt-2">{submitError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="mt-4 w-full py-3 px-8 bg-primary hover:bg-primary-darker disabled:opacity-60 rounded-lg font-semibold text-white text-sm"
                  >
                    <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                    {submitting ? 'Sending…' : 'Send request'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoBookingModal;
